// router/routes.ts
import type { RouteRecordRaw } from "vue-router";
import { AdminLevel } from "@/helpers/types/adminLevel";
import { appAuthorWebsite, discordInviteLinkForServers } from "@/helpers";

export interface RouteMetaType {
  title?: string;
  requiresAuth?: boolean;
  requiresServerSelection?: boolean;
  requiresPanelSelection?: boolean;
  requiresAdmin?: boolean;
  requiredAdminLevels?: AdminLevel[];
  hideBotAlert?: boolean;
  skipServerAccessCheck?: boolean;
}

declare module "vue-router" {
  interface RouteMeta extends RouteMetaType {}
}

const ALL_ADMINS = [AdminLevel.MODERATOR, AdminLevel.BILLING_MANAGER, AdminLevel.SUPER_ADMIN];
const BILLING_ADMINS = [AdminLevel.BILLING_MANAGER, AdminLevel.SUPER_ADMIN];
const SUPER_ADMINS = [AdminLevel.SUPER_ADMIN];

export const allRoutes: RouteRecordRaw[] = [
  // --- PUBLIC ROUTES (Specific Titles) ---
  {
    path: "/",
    name: "landing",
    component: () => import("@/views/landing/index.vue"),
    // No title -> resolves to base "TicketForge"
  },
  {
    path: "/login",
    name: "auth.login",
    component: () => import("@/views/pages/login/index.vue"),
    meta: { title: "Log In", hideBotAlert: true },
  },
  {
    path: "/auth/close",
    name: "AuthPopupClose",
    component: () => import("@/views/pages/discordauth/AuthPopupClose.vue"),
  },
  {
    path: "/terms-of-service",
    name: "legal.terms",
    component: () => import("@/views/legal/TermsOfService.vue"),
    meta: { title: "Terms of Service" },
  },
  {
    path: "/privacy-policy",
    name: "legal.privacy",
    component: () => import("@/views/legal/PrivacyPolicy.vue"),
    meta: { title: "Privacy Policy" },
  },
  {
    path: "/refund-policy",
    name: "legal.refund",
    component: () => import("@/views/legal/RefundPolicy.vue"),
    meta: { title: "Refund Policy" },
  },
  {
    path: "/legal-notice",
    name: "legal.notice",
    component: () => import("@/views/legal/LegalNotice.vue"),
    meta: { title: "Legal Notice" },
  },
  {
    path: "/get-premium",
    name: "get-premium",
    component: () => import("@/views/pages/get-premium/HowToPremium.vue"),
    meta: { title: "Get Premium" },
  },
  {
    path: "/links",
    name: "pages.links",
    component: () => import("@/views/pages/links/index.vue"),
    meta: { title: "Useful Links & Bot Invite" },
  },
  {
    path: "/support",
    name: "support",
    component: () => import("@/views/InviteRedirect.vue"),
    props: { redirectTo: appAuthorWebsite },
  },
  {
    path: "/invite",
    name: "invite",
    component: () => import("@/views/InviteRedirect.vue"),
    props: { redirectTo: discordInviteLinkForServers },
  },
  {
    path: "/apply/:urlToken",
    name: "public.apply",
    component: () => import("@/views/pages/apply/index.vue"),
    meta: { title: "Apply", hideBotAlert: true },
  },
  {
    path: "/verify/:guildId",
    name: "public.verify",
    component: () => import("@/views/pages/verify/index.vue"),
    meta: { 
      title: "Verification", 
      hideBotAlert: true,
      skipServerAccessCheck: true
    },
  },

  // --- DASHBOARD (Resolves to "TicketForge | Dashboard") ---
  {
    path: "/dashboard",
    meta: {
      title: "Dashboard",
      requiresAuth: true,
    },
    children: [
      {
        path: "servers",
        name: "pages.servers",
        component: () => import("@/views/pages/servers/index.vue"),
        meta: { hideBotAlert: true },
      },
      {
        path: "usersubscriptions",
        name: "pages.usersubscriptions",
        component: () => import("@/views/pages/usersubscriptions/index.vue"),
        meta: { hideBotAlert: true },
      },
      // --- SERVER-SPECIFIC ROUTES ---
      {
        path: "server/:guildId",
        children: [
          {
            path: "panels",
            name: "pages.panels",
            component: () => import("@/views/pages/panels/index.vue"),
          },
          {
            path: "statistics",
            name: "pages.statistics",
            component: () => import("@/views/pages/statistics/index.vue"),
          },
          {
            path: "history",
            name: "pages.history",
            component: () => import("@/views/pages/history/index.vue"),
          },
          {
            path: "commands",
            name: "pages.commands",
            component: () => import("@/views/pages/commands/index.vue"),
          },
          {
            path: "premium",
            name: "pages.premium",
            component: () => import("@/views/pages/premium/index.vue"),
          },
          {
            path: "roadmap",
            name: "pages.roadmap",
            component: () => import("@/views/pages/roadmap/index.vue"),
          },
          {
            path: "config",
            name: "pages.serverconfig",
            component: () => import("@/views/pages/serverconfig/index.vue"),
          },
          // --- PANEL & APP EDITORS ---
          {
            path: "panel/:panelId",
            name: "pages.panel.edit",
            component: () => import("@/views/pages/editpanel/index.vue"),
          },
          {
            path: "applications",
            name: "pages.applications",
            component: () => import("@/views/pages/applications/index.vue"),
          },
          {
            path: "application/:applicationId",
            name: "pages.application.edit",
            component: () => import("@/views/pages/editapplication/index.vue"),
          },
          {
            path: "submissions",
            name: "pages.applications.submissions",
            component: () => import("@/views/pages/applicationsubmissions/index.vue"),
          },
          {
            path: "verification",
            name: "pages.verification",
            component: () => import("@/views/pages/verification/index.vue"),
          },
        ],
      },

      // --- ADMIN SECTION (Overrides Dashboard -> "TicketForge | Admin") ---
      {
        path: "moderation",
        meta: {
          title: "Admin",
          requiresAdmin: true,
          requiresServerSelection: false,
        },
        children: [
          {
            path: "feedback",
            name: "moderation.feedback",
            component: () => import("@/views/pages/moderationfeedback/index.vue"),
            meta: { requiredAdminLevels: ALL_ADMINS },
          },
          {
            path: "manage",
            name: "moderation.manage",
            component: () => import("@/views/pages/moderationtemplates/index.vue"),
            meta: { requiredAdminLevels: ALL_ADMINS },
          },
          {
            path: "edit-template/:templateId",
            name: "moderation.edit-template",
            component: () => import("@/views/pages/editpanel/index.vue"),
            meta: { requiredAdminLevels: ALL_ADMINS },
          },
          {
            path: "log",
            name: "moderation.log",
            component: () => import("@/views/pages/moderationlogs/index.vue"),
            meta: { requiredAdminLevels: ALL_ADMINS },
          },
          {
            path: "subscriptions",
            name: "moderation.subscriptions",
            component: () => import("@/views/pages/moderationsubscriptions/index.vue"),
            meta: { requiredAdminLevels: BILLING_ADMINS },
          },
          {
            path: "admins",
            name: "moderation.admins",
            component: () => import("@/views/pages/moderationadmins/index.vue"),
            meta: { requiredAdminLevels: SUPER_ADMINS },
          },
          {
            path: "lookup",
            name: "moderation.lookup",
            component: () => import("@/views/pages/moderationlookup/index.vue"),
            meta: {
              requiredAdminLevels: ALL_ADMINS,
              skipServerAccessCheck: true,
            },
          },
          {
            path: "edit-app-template/:templateId",
            name: "moderation.edit-app-template",
            component: () => import("@/views/pages/editapplication/index.vue"),
            meta: { requiredAdminLevels: ALL_ADMINS },
          },
        ],
      },
    ],
  },
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    component: () => import("@/views/pages/notfound/index.vue"),
    meta: { title: "404" },
  },
];